import {
  SHADOW_ROOT_XPATH_SEPARATOR,
  SVG_NAMESPACE_PREFIX,
  SVG_NAMESPACE_RESOLVER,
} from '../constants';

export function createXPathFromElement(
  elm: HTMLElement | SVGElement
): string | null {
  let segments;
  for (
    segments = [];
    elm &&
    (elm.nodeType === Node.ELEMENT_NODE ||
      elm.nodeType === Node.DOCUMENT_FRAGMENT_NODE);
    elm =
      elm.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
        ? (elm.parentNode as HTMLElement)
        : ((elm as unknown as ShadowRoot).host as HTMLElement)
  ) {
    let i;
    let sibling;
    for (
      i = 1, sibling = elm.previousSibling;
      sibling;
      sibling = sibling.previousSibling
    ) {
      if (sibling.localName === elm.localName) {
        i++;
      }
    }

    segments.unshift(
      `${getSegmentNamespacePrefix(elm)}${getSegmentName(elm, i)}`
    );
  }

  return segments.length ? `/${segments.join('/')}` : null;
}

export function createFullXpathFromNestedElement(elm: HTMLElement): string {
  if (document === elm.ownerDocument) {
    return createXPathFromElement(elm);
  }
  const pathToElement: string[] = [];

  const generatePathToElement = (
    element: HTMLElement,
    isFirstRun?: boolean
  ) => {
    if (element.ownerDocument === document) {
      return;
    }

    const iframe = element.ownerDocument?.defaultView
      ?.frameElement as HTMLIFrameElement;
    if (!iframe) {
      return;
    }

    const xpathToIframe = createXPathFromElement(iframe);
    const xpathToElement = isFirstRun ? createXPathFromElement(element) : '';
    pathToElement.push(xpathToIframe + xpathToElement);
    generatePathToElement(iframe.parentElement);
  };

  generatePathToElement(elm, true);
  return pathToElement.reverse().join('');
}

export function getTargetElementFromNestedXpath(
  xpath: string,
  documentRoot: Document = document
): { targetElement: HTMLElement; documentRoot: Document } {
  if (!xpath) {
    return {
      documentRoot: documentRoot,
      targetElement: documentRoot.body,
    };
  }
  const xpaths = getSeparatedXpathByFrameOrIframe(xpath);
  let currDocument = documentRoot;
  let targetEl;

  xpaths.forEach((xpath) => {
    if (xpath.includes(SHADOW_ROOT_XPATH_SEPARATOR)) {
      targetEl = getEditElementFromShadowRoot(xpath, currDocument);
      if (targetEl) {
        if (targetEl.tagName === 'SLOT') {
          const assignedNodes = (targetEl as HTMLSlotElement).assignedNodes();
          if (assignedNodes.length) {
            const assignedElement = assignedNodes[0] as HTMLElement;
            targetEl = assignedElement;

            // Since we can't target the TEXT_NODE we are going one element above <slot>
            if (assignedElement.nodeType === Node.TEXT_NODE) {
              targetEl = targetEl.parentElement;
            }
          }
        }
        currDocument = targetEl?.contentDocument || targetEl?.ownerDocument;
      }
      return;
    }
    targetEl = currDocument.evaluate(
      xpath,
      currDocument,
      (prefix) => {
        if (prefix === 'svg') {
          return SVG_NAMESPACE_RESOLVER;
        }
      },
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue as HTMLElement;
    if (targetEl) {
      currDocument = targetEl?.contentDocument || targetEl?.ownerDocument;
    }
  });

  if (!targetEl) {
    return {
      documentRoot: currDocument,
      targetElement: currDocument.body,
    };
  }

  return {
    targetElement: targetEl,
    documentRoot: currDocument,
  };
}

function lookupElementByXPath(
  xpath: string,
  fromElement?: HTMLElement,
  documentRoot: Document = document
): HTMLElement | null {
  if (!xpath) {
    return null;
  }

  if (xpath.includes(SHADOW_ROOT_XPATH_SEPARATOR)) {
    return getEditElementFromShadowRoot(xpath, documentRoot);
  }

  try {
    return documentRoot.evaluate(
      xpath,
      fromElement ? fromElement : documentRoot,
      (prefix) => {
        if (prefix === 'svg') {
          return SVG_NAMESPACE_RESOLVER;
        }
      },
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    )?.singleNodeValue as HTMLElement;
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

function getEditElementFromShadowRoot(
  xpath: string,
  documentRoot: Document = document
): HTMLElement {
  const xpaths = xpath.split(SHADOW_ROOT_XPATH_SEPARATOR);

  const hostElementXpath = xpaths.shift();
  let element = lookupElementByXPath(hostElementXpath, undefined, documentRoot);
  const shadowRootChildren = element?.shadowRoot?.children || element?.children;

  if (!shadowRootChildren) {
    return element;
  }

  let rootChildren = Array.from(shadowRootChildren);
  xpaths.forEach((xpath: string) => {
    const xpathTags = xpath.split('/').filter((str) => str !== '');

    xpathTags.forEach((tag, index) => {
      const tagParts = tag.split('[');
      const localName = removeSegmentNamespacePrefix(tagParts[0]);
      let elementIndex = Number(tagParts[1].replace(']', '')) - 1;
      const childrenElements = rootChildren.filter(
        (child) => child.localName === localName
      ) as HTMLElement[];

      if (elementIndex < 0) {
        console.debug('shadow root element index < 0');
        elementIndex = 0;
      }

      const isLastTag = xpathTags.length - 1 === index;
      const currentElement = childrenElements?.[elementIndex];
      if (!isLastTag) {
        if (currentElement?.children) {
          const shadowChildren = currentElement?.shadowRoot?.children
            ? Array.from(currentElement?.shadowRoot?.children)
            : [];
          const domChildren = Array.from(currentElement.children);
          // sometimes user can click on shadow children, sometimes on regular children
          // so we have to return both
          rootChildren = [...domChildren, ...shadowChildren];
        }
      } else {
        element = currentElement;
        if (element?.shadowRoot?.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          rootChildren = Array.from(element?.shadowRoot?.children);
        }
      }
    });
  });

  return element;
}

function getSeparatedXpathByFrameOrIframe(xpath: string) {
  const xpathArr = xpath.split(/(\/i?frame\[\d+\])/g);
  const xpathArrCorrect = [];
  for (let i = 0; i <= xpathArr.length; i += 2) {
    if (xpathArr[i + 1]) {
      xpathArrCorrect.push(xpathArr[i] + xpathArr[i + 1]);
    } else {
      xpathArrCorrect.push(xpathArr[i]);
    }
  }
  return xpathArrCorrect;
}

function getSegmentNamespacePrefix(element: HTMLElement | SVGElement) {
  if (
    (element as SVGElement).ownerSVGElement ||
    element?.tagName?.toLowerCase() === 'svg'
  ) {
    return SVG_NAMESPACE_PREFIX;
  }

  return '';
}

function getSegmentName(element: HTMLElement | SVGElement, index) {
  let segmentName = `${element?.localName}[${index}]`;
  if (element?.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    segmentName = '+';
  }

  return segmentName;
}

function removeSegmentNamespacePrefix(xpathSegment: string) {
  return xpathSegment.replace(SVG_NAMESPACE_PREFIX, '');
}
